import { useEffect, useState } from 'react';

import { getRegionContextOnce, applyRegionalPrice, originalPriceFor } from 'data/services/regionalPricing';

/**
 * The learner's PPP region, fetched once, plus a helper that prices an item.
 *
 * Fails silently to "no discount". Every failure mode here -- flag off,
 * unmapped country, endpoint down -- must leave list prices exactly as they
 * were, because showing a discount the checkout will not honour is far worse
 * than showing none.
 */
const useRegionalPricing = () => {
  const [context, setContext] = useState(null);

  useEffect(() => {
    let active = true;
    // Shared across every card on the page -- see getRegionContextOnce.
    getRegionContextOnce()
      .then((data) => { if (active) { setContext(data); } })
      .catch(() => { if (active) { setContext(null); } });
    return () => { active = false; };
  }, []);

  const discountPct = context?.enabled ? (context.discountPct || 0) : 0;

  return {
    discountPct,
    region: context?.region ?? null,
    nativeCurrency: context?.nativeCurrency ?? null,
    fxRate: context?.fxRate ?? null,
    /**
     * @returns {{price:number, originalPrice:number, discountPct:number,
     *            nativeAmount:number|null, nativeCurrency:string|null}}
     */
    priceFor: (baseAmount) => {
      const price = applyRegionalPrice(baseAmount, discountPct);
      const rate = context?.fxRate;
      return {
        price,
        originalPrice: originalPriceFor(baseAmount, discountPct),
        discountPct,
        nativeAmount: rate ? Math.round(price * rate * 100) / 100 : null,
        nativeCurrency: context?.nativeCurrency ?? null,
      };
    },
  };
};

export default useRegionalPricing;
